import { useMemo, type ReactNode } from 'react';
import { motion } from 'framer-motion';
import { ConfigProvider, DatePicker, Select } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { Search } from 'lucide-react';
import { OMS_COLORS, SOVEREIGN_CONTROL_THEME, sectionVariants } from '../omsDashTheme';
import { RANGE_PRESETS, windowLabel, type RangePreset } from '../utils/scopeWindow';
import type { PerformanceModel } from '../utils/performanceModel';
import type { ReportPayload } from '../utils/exportReport';
import type { Scope, ScopeActions } from '../hooks/useScope';
import ExportMenu from './ExportMenu';

const { RangePicker } = DatePicker;

interface Props {
  scope: Scope;
  actions: ScopeActions;
  model: PerformanceModel;
  /** True while a scope change is recomputing the page. */
  refreshing: boolean;
  getReport: () => ReportPayload | null;
  /** When provided, replaces the default ExportMenu (Manager gets the Daily Report button). */
  exportSlot?: ReactNode;
}

/**
 * Global scope bar — the one control surface every number on the page
 * obeys. Time presets + custom range, location & project filters, entity
 * search and the report download. Sticky so scope is always at hand.
 */
export default function ScopeBar({ scope, actions, model, refreshing, getReport, exportSlot }: Props) {
  const stateOptions = useMemo(
    () =>
      model.states.map((s) => ({
        value: s.name,
        label: `${s.name} (${s.projectCount})`,
      })),
    [model.states]
  );

  const locationOptions = useMemo(
    () =>
      model.locations
        .filter((l) => !scope.state || l.stateName === scope.state)
        .map((l) => ({ value: l.id, label: `${l.name} (${l.projectCount})` })),
    [model.locations, scope.state]
  );

  const projectOptions = useMemo(
    () =>
      model.projects
        .filter(
          (p) =>
            (!scope.state || p.stateName === scope.state) &&
            (!scope.locationId || (p.locationId ?? 'unmapped') === scope.locationId)
        )
        .map((p) => ({ value: p.id, label: p.name })),
    [model.projects, scope.state, scope.locationId]
  );

  const customValue: [Dayjs, Dayjs] | null =
    scope.preset === 'custom' ? [dayjs(scope.window.from), dayjs(scope.window.to)] : null;

  return (
    <motion.section
      variants={sectionVariants}
      initial="hidden"
      animate="visible"
      custom={1}
      aria-label="Global scope"
      className="sticky top-16 z-30 -mx-1 px-1 lg:top-[80px]"
    >
      <div
        className="flex flex-wrap items-center gap-2 rounded-2xl border border-[rgba(11,59,46,0.28)] px-3 py-2 shadow-[0_10px_30px_-18px_rgba(42,31,23,0.35)]"
        style={{ backgroundColor: 'rgba(250,247,238,0.97)' }}
      >
        {/* Time presets */}
        <div
          className="flex items-center gap-0.5 rounded-xl border border-[rgba(11,59,46,0.24)] p-0.5"
          style={{ backgroundColor: '#fcfaf3' }}
          role="group"
          aria-label="Time window"
        >
          {RANGE_PRESETS.filter((p) => p.key !== 'custom').map((p) => {
            const active = scope.preset === p.key;
            return (
              <button
                key={p.key}
                type="button"
                aria-pressed={active}
                onClick={() => actions.setPreset(p.key as Exclude<RangePreset, 'custom'>)}
                className={`rounded-lg px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wide transition ${
                  active ? 'bg-[#264f3c] text-white shadow-sm' : 'text-[#5A6275] hover:text-[#1F5A47]'
                }`}
              >
                {p.label}
              </button>
            );
          })}
        </div>

        <ConfigProvider theme={SOVEREIGN_CONTROL_THEME}>
          <RangePicker
            size="small"
            value={customValue}
            placeholder={['Custom from', 'to']}
            format="DD MMM YYYY"
            disabledDate={(d) => d.isAfter(dayjs(), 'day')}
            onChange={(dates) => {
              if (dates?.[0] && dates[1]) {
                actions.setCustomWindow({
                  from: dates[0].format('YYYY-MM-DD'),
                  to: dates[1].format('YYYY-MM-DD'),
                });
              } else {
                actions.setPreset('week');
              }
            }}
          />

          <Select
            size="small"
            allowClear
            showSearch
            placeholder="All locations"
            aria-label="Filter by location"
            value={scope.state ?? undefined}
            options={stateOptions}
            optionFilterProp="label"
            style={{ minWidth: 150 }}
            onChange={(value) => {
              if (value) actions.setSelection({ state: value, locationId: null, projectId: null });
              else actions.drillToLevel(1);
            }}
          />

          <Select
            size="small"
            allowClear
            showSearch
            placeholder="All sites"
            aria-label="Filter by site location"
            value={scope.locationId ?? undefined}
            options={locationOptions}
            optionFilterProp="label"
            style={{ minWidth: 150 }}
            onChange={(value) => {
              if (value) {
                // Selecting a location locks its state so the breadcrumb
                // stays truthful.
                const location = model.locations.find((l) => l.id === value);
                actions.setSelection({
                  state: location?.stateName ?? scope.state,
                  locationId: value,
                  projectId: null,
                });
              } else {
                actions.drillToLevel(2);
              }
            }}
          />

          <Select
            size="small"
            allowClear
            showSearch
            placeholder="All projects"
            aria-label="Filter by project"
            value={scope.projectId ?? undefined}
            options={projectOptions}
            optionFilterProp="label"
            style={{ minWidth: 170 }}
            onChange={(value) => {
              if (value) {
                // Selecting a project locks its location + state.
                const project = model.projects.find((p) => p.id === value);
                actions.setSelection({
                  state: project?.stateName ?? scope.state,
                  locationId: project ? (project.locationId ?? 'unmapped') : scope.locationId,
                  projectId: value,
                });
              } else {
                actions.drillToLevel(3);
              }
            }}
          />
        </ConfigProvider>

        {/* Entity search */}
        <label className="relative">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2"
            style={{ color: OMS_COLORS.caption }}
          />
          <input
            type="search"
            value={scope.query}
            onChange={(e) => actions.setQuery(e.target.value)}
            placeholder="Search location, project, engineer…"
            aria-label="Search entities"
            className="w-52 rounded-xl border border-[rgba(11,59,46,0.24)] bg-[#fcfaf3] py-1.5 pl-8 pr-2 text-xs font-semibold text-[#2A1F17] outline-none transition placeholder:text-[#7C8A82] focus:border-[#1F5A47] focus:ring-2 focus:ring-[rgba(51,146,116,0.25)]"
          />
        </label>

        {/* Window recap + download, right-aligned */}
        <div className="ml-auto flex items-center gap-3">
          <p className="hidden items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.12em] sm:flex" style={{ color: OMS_COLORS.champagneDeep }}>
            {windowLabel(scope.window)}
            {refreshing && (
              <span
                className="inline-block h-1.5 w-1.5 animate-pulse rounded-full"
                style={{ backgroundColor: OMS_COLORS.amber }}
                aria-label="Refreshing"
              />
            )}
          </p>
          {exportSlot ?? <ExportMenu getPayload={getReport} />}
        </div>
      </div>
    </motion.section>
  );
}
